import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Clock, CheckCircle2, ArrowLeft } from "lucide-react";
import api from "../../lib/api";
import { useT, useUI, useLang } from "../../lib/i18n";

/**
 * Booking widget — picks date+slot from admin-defined availability, then collects contact info.
 */
export default function BookingWidget({ content = {} }) {
  const t = useT();
  const ui = useUI();
  const lang = useLang();
  const cfg = content.booking_form || {};

  const [slots, setSlots] = useState([]);
  const [step, setStep] = useState("date"); // date | time | form | success
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [status, setStatus] = useState({ loading: false, error: "" });

  useEffect(() => {
    api.get("/availability").then(({ data }) => {
      const open = (data || []).filter((s) => !s.booked);
      setSlots(open);
    }).catch(() => {});
  }, []);

  // Group available slots by date
  const dates = useMemo(() => {
    const map = new Map();
    slots.forEach((s) => {
      if (!map.has(s.date)) map.set(s.date, []);
      map.get(s.date).push(s);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [slots]);

  const slotsForDate = selectedDate ? slots.filter((s) => s.date === selectedDate).sort((a, b) => a.time.localeCompare(b.time)) : [];

  const fmtDate = (iso) => {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { weekday: "long", day: "numeric", month: "long" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, error: "" });
    try {
      await api.post("/bookings", { slot_id: selectedSlot.id, ...form });
      setStep("success");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({ loading: false, error: typeof detail === "string" ? detail : "Erreur" });
    } finally {
      setStatus((s) => ({ ...s, loading: false }));
    }
  };

  const reset = () => {
    setStep("date");
    setSelectedDate(null);
    setSelectedSlot(null);
    setForm({ name: "", email: "", phone: "", service: "", message: "" });
  };

  if (dates.length === 0 && step === "date") {
    return (
      <div className="bg-[#FDFBF7] rounded-3xl border border-[#2D2A26]/10 p-8 text-center" data-testid="booking-empty">
        <Calendar size={32} className="text-[#C87A5E] mx-auto mb-3" />
        <p className="text-[#4A3F35]">
          {lang === "fr"
            ? "Aucun créneau disponible pour le moment. Utilisez le formulaire de contact ci-dessous pour réserver."
            : "No available slot at the moment. Use the contact form below to book."}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#FDFBF7] rounded-3xl border border-[#2D2A26]/10 overflow-hidden" data-testid="booking-widget">
      <div className="bg-[#2D2A26] text-[#FDFBF7] px-8 py-6">
        <p className="overline !text-[#C87A5E]">{t(cfg.overline)}</p>
        <h3 className="font-serif text-2xl md:text-3xl mt-1">{t(cfg.title)}</h3>
        <p className="text-[#FDFBF7]/70 text-sm mt-2 max-w-lg">{t(cfg.description)}</p>
      </div>

      <div className="p-6 md:p-8 min-h-[320px]">
        <AnimatePresence mode="wait">
          {step === "date" && (
            <motion.div key="date" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <p className="overline mb-4 flex items-center gap-2"><Calendar size={14} /> {lang === "fr" ? "1. Choisissez une date" : "1. Pick a date"}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                {dates.map(([date, daySlots]) => (
                  <button
                    key={date}
                    onClick={() => { setSelectedDate(date); setStep("time"); }}
                    className="text-left p-4 rounded-2xl border border-[#2D2A26]/10 hover:border-[#C87A5E] hover:bg-[#F6F3EC] transition group"
                    data-testid={`booking-date-${date}`}
                  >
                    <div className="text-xs text-[#C87A5E] uppercase tracking-wider">{new Date(date + "T00:00:00").toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { weekday: "short" })}</div>
                    <div className="font-serif text-2xl mt-1">{new Date(date + "T00:00:00").getDate()}</div>
                    <div className="text-xs text-[#4A3F35]/70 mt-1">{new Date(date + "T00:00:00").toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { month: "short" })}</div>
                    <div className="text-xs text-[#8A9A86] mt-2">{daySlots.length} {daySlots.length > 1 ? (lang === "fr" ? "créneaux" : "slots") : (lang === "fr" ? "créneau" : "slot")}</div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === "time" && (
            <motion.div key="time" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <button onClick={() => setStep("date")} className="inline-flex items-center gap-1 text-xs text-[#4A3F35]/70 mb-4 hover:text-[#C87A5E]"><ArrowLeft size={12} /> {lang === "fr" ? "Changer de date" : "Change date"}</button>
              <p className="overline mb-2 flex items-center gap-2"><Clock size={14} /> {lang === "fr" ? "2. Choisissez un horaire" : "2. Pick a time"}</p>
              <p className="text-sm text-[#4A3F35] mb-4 capitalize">{fmtDate(selectedDate)}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                {slotsForDate.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => { setSelectedSlot(s); setStep("form"); setForm({ ...form, service: s.service || form.service }); }}
                    className="p-3 rounded-xl border border-[#2D2A26]/10 hover:border-[#C87A5E] hover:bg-[#F6F3EC] transition text-center"
                    data-testid={`booking-time-${s.id}`}
                  >
                    <div className="font-serif text-xl">{s.time}</div>
                    <div className="text-xs text-[#4A3F35]/60 mt-0.5">{s.duration_minutes} min</div>
                    {s.service && <div className="text-xs text-[#C87A5E] mt-1">{s.service}</div>}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === "form" && selectedSlot && (
            <motion.form key="form" onSubmit={submit} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="booking-form">
              <button type="button" onClick={() => setStep("time")} className="inline-flex items-center gap-1 text-xs text-[#4A3F35]/70 mb-4 hover:text-[#C87A5E]"><ArrowLeft size={12} /> {lang === "fr" ? "Changer d'horaire" : "Change time"}</button>
              <div className="bg-[#F6F3EC] rounded-xl p-4 mb-5 flex items-center gap-3 text-sm">
                <Calendar size={18} className="text-[#C87A5E]" />
                <span className="font-medium capitalize">{fmtDate(selectedSlot.date)} · {selectedSlot.time}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input label={ui.yourName} value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="bk-name" />
                <Input label={ui.email} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="bk-email" />
                <Input label={ui.phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="bk-phone" />
                <div>
                  <label className="overline block mb-1.5">{ui.sessionType}</label>
                  <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="w-full bg-transparent border-b border-[#2D2A26]/30 py-2 text-sm">
                    <option value="">{ui.chooseOption}</option>
                    <option value="Familiale">{ui.famille}</option>
                    <option value="Couple">{ui.couple}</option>
                    <option value="Naissance">{ui.naissance}</option>
                    <option value="Grossesse">{ui.grossesse}</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="overline block mb-1.5">{ui.yourMessage} ({lang === "fr" ? "optionnel" : "optional"})</label>
                <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-transparent border-b border-[#2D2A26]/30 py-2 text-sm resize-none" />
              </div>
              {status.error && <div className="mt-4 text-sm text-red-700" data-testid="booking-error">{status.error}</div>}
              <button type="submit" disabled={status.loading} className="btn-primary mt-6 disabled:opacity-50" data-testid="booking-submit">
                {status.loading ? ui.sending : (lang === "fr" ? "Demander la réservation" : "Request booking")}
              </button>
            </motion.form>
          )}

          {step === "success" && (
            <motion.div key="success" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center py-8" data-testid="booking-success">
              <CheckCircle2 size={56} className="text-[#8A9A86] mx-auto mb-4" />
              <h3 className="font-serif text-3xl">{lang === "fr" ? "Demande envoyée !" : "Request sent!"}</h3>
              <p className="text-[#4A3F35] mt-3 max-w-md mx-auto">
                {lang === "fr"
                  ? "Votre demande de réservation est bien reçue. Vous recevrez une confirmation par email sous 48h."
                  : "Your booking request has been received. You'll get a confirmation by email within 48 hours."}
              </p>
              <button onClick={reset} className="btn-ghost mt-6">
                {lang === "fr" ? "Faire une autre demande" : "Submit another request"}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-transparent border-b border-[#2D2A26]/30 py-2 text-sm focus:border-[#C87A5E] outline-none"
        data-testid={testid}
      />
    </div>
  );
}
